import { Injectable, NotFoundException } from '@nestjs/common';
import { EntityManager, wrap } from '@mikro-orm/core';
import { Chapter } from '../entities/chapter.entity';
import { CreateChapterDto } from '../dto/create-chapter.dto';
import { UpdateChapterDto } from '../dto/update-chapter.dto';
import { User } from '../../user/entities/user.entity';
import { Newsfeed } from '../../newsfeed/entities/newsfeed.entity';

@Injectable()
export class ChapterService {
  constructor(private readonly em: EntityManager) {}

  async create(createDto: CreateChapterDto, user: User): Promise<any> {
    const chapter = new Chapter();
    this.em.assign(chapter, { title: createDto.title, user });
    await this.em.persistAndFlush(chapter);

    if (createDto.newsfeeds && createDto.newsfeeds.length > 0) {
      const newsfeeds = await this.em.find(Newsfeed, { id: { $in: createDto.newsfeeds } });
      newsfeeds.forEach((n) => (n.chapter = chapter));
      await this.em.flush();
    }

    // reload with relations populated
    const reloaded = await this.em.findOne(Chapter, { id: chapter.id }, { populate: ['user', 'newsfeeds.tags'] });
    return wrap(reloaded!).toJSON();
  }

  async findAll(page = 1, limit = 10): Promise<{ data: any[]; page: number | null; limit: number | null; total: number | null }> {
    const chapters = await this.em.find(
      Chapter,
      {},
      { populate: ['user', 'newsfeeds.tags'], limit, offset: (page - 1) * limit, orderBy: { created_at: 'DESC' } }
    );

    return {
      data: chapters.map((c) => wrap(c).toJSON()),
      page,
      limit,
      total: await this.em.count(Chapter),
    };
  }

  async findOne(id: number): Promise<any> {
    const chapter = await this.em.findOne(Chapter, { id }, { populate: ['user', 'newsfeeds.tags'], refresh: true });
    if (!chapter) {
      throw new NotFoundException(`Chapter with ID "${id}" not found`);
    }
    return wrap(chapter).toJSON();
  }

  async update(id: number, updateDto: UpdateChapterDto): Promise<any> {
    const chapter = await this.em.findOne(Chapter, { id }, { populate: ['user', 'newsfeeds.tags'] });
    if (!chapter) {
      throw new NotFoundException(`Chapter with ID "${id}" not found`);
    }

    if (updateDto.title !== undefined) {
      chapter.title = updateDto.title;
    }

    if (updateDto.newsfeeds) {
      // Detach existing newsfeeds not in the new list
      const current = await this.em.find(Newsfeed, { chapter: { id } });
      const newSet = new Set(updateDto.newsfeeds);
      for (const n of current) {
        if (!newSet.has(n.id)) {
          n.chapter = undefined;
        }
      }
      // Attach new ones
      if (updateDto.newsfeeds.length > 0) {
        const toAttach = await this.em.find(Newsfeed, { id: { $in: updateDto.newsfeeds } });
        toAttach.forEach((n) => (n.chapter = chapter));
      }
    }

    await this.em.flush();
    return wrap(chapter).toJSON();
  }

  async remove(id: number): Promise<void> {
    const chapter = await this.em.findOne(Chapter, { id });
    if (!chapter) {
      throw new NotFoundException(`Chapter with ID "${id}" not found`);
    }
    await this.em.removeAndFlush(chapter);
  }
}
